import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, Check, Loader2, Pencil, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [];
const now = new Date();
const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
for (let m = 0; m < 12; m++) {
  const month = ((3 + m) % 12) + 1;
  const year = month >= 4 ? fyStart : fyStart + 1;
  MONTHS.push({
    value: `${year}-${String(month).padStart(2, "0")}-01`,
    key: `${year}-${String(month).padStart(2, "0")}`,
    label: new Date(year, month - 1, 1).toLocaleString("en-IN", { month: "short" }),
    yearLabel: String(year).slice(2),
  });
}

// ─── Toast (top of screen) ──────────────────────────────────
function Toast({ message, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2400);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] text-white text-xs font-semibold px-5 py-2.5 rounded-xl shadow-lg flex items-center gap-2 ${
        type === "success" ? "bg-emerald-600" : "bg-red-600"
      }`}
      style={{ animation: "fadeSlideDown .25s ease-out" }}
      data-testid="target-toast"
    >
      {type === "success" ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
      {message}
    </div>
  );
}

// ─── Single Cell ─────────────────────────────────────────────
// States: idle → editing → confirming → saving → idle
function MatrixCell({ origValue, origCount, isSales, projectName, period, onSaved, onToast }) {
  const [phase, setPhase] = useState("idle"); // idle | editing | confirming | saving
  const [editVal, setEditVal] = useState("");
  const [editCnt, setEditCnt] = useState("");
  const inputRef = useRef(null);

  const hasOrig = origValue != null && origValue !== "";

  // focus input when entering edit mode
  useEffect(() => {
    if (phase === "editing" && inputRef.current) inputRef.current.focus();
  }, [phase]);

  const openEdit = () => {
    setEditVal(hasOrig ? String(origValue) : "");
    setEditCnt(origCount != null ? String(origCount) : "");
    setPhase("editing");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); setPhase("confirming"); }
    if (e.key === "Escape") { e.preventDefault(); setPhase("idle"); }
  };

  const handleCancel = () => setPhase("idle");

  const handleSave = async () => {
    setPhase("saving");
    const numVal = parseFloat(editVal);
    const numCnt = isSales ? parseInt(editCnt) || 0 : null;
    try {
      await onSaved(projectName, period, isNaN(numVal) ? null : numVal, numCnt);
      setPhase("idle");
      onToast("Saved", "success");
    } catch {
      setPhase("idle");
      onToast("Save Failed", "error");
    }
  };

  // ── Editing mode: input field ──
  if (phase === "editing") {
    return (
      <div className="flex flex-col gap-0.5 px-0.5" data-testid="cell-editing">
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Cr"
          className="w-full h-7 px-1.5 text-[11px] text-right bg-white border-2 border-blue-500 rounded focus:outline-none font-semibold tabular-nums"
          data-testid="cell-input-amount"
        />
        {isSales && (
          <input
            type="number"
            value={editCnt}
            onChange={(e) => setEditCnt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Units"
            className="w-full h-5 px-1.5 text-[9px] text-right bg-white border border-blue-300 rounded focus:outline-none text-slate-500 tabular-nums"
            data-testid="cell-input-count"
          />
        )}
      </div>
    );
  }

  // ── Confirming mode: show new value + Save / Cancel ──
  if (phase === "confirming") {
    const displayVal = editVal || "–";
    return (
      <div className="flex flex-col items-end px-1.5 py-0.5 bg-amber-50 ring-1 ring-amber-300 rounded-[3px]" data-testid="cell-confirming">
        <span className="text-[11px] font-semibold text-amber-800 tabular-nums">{displayVal}</span>
        {isSales && editCnt && <span className="text-[9px] text-amber-600 tabular-nums">{editCnt} units</span>}
        <div className="flex items-center gap-1 mt-1">
          <button
            onClick={handleSave}
            className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded transition-colors"
            data-testid="cell-save-btn"
          >
            <Check className="h-2.5 w-2.5" />Save
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold text-slate-600 bg-slate-200 hover:bg-slate-300 rounded transition-colors"
            data-testid="cell-cancel-btn"
          >
            <X className="h-2.5 w-2.5" />Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Saving mode: spinner ──
  if (phase === "saving") {
    return (
      <div className="flex items-center justify-center min-h-[36px] px-2 py-1">
        <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
      </div>
    );
  }

  // ── Idle mode: display value + pencil on hover ──
  return (
    <div
      className="group relative h-full min-h-[36px] flex flex-col items-end justify-center px-2 py-1 rounded-[3px] transition-colors cursor-default select-none"
      data-testid="cell-display"
    >
      <span className={`text-[11px] tabular-nums leading-tight ${hasOrig ? "font-semibold text-slate-800" : "text-slate-300 font-normal"}`}>
        {hasOrig ? origValue : "–"}
      </span>
      {isSales && hasOrig && origCount != null && (
        <span className="text-[9px] tabular-nums text-slate-400 leading-tight">{origCount} units</span>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); openEdit(); }}
        className="absolute top-0.5 right-0.5 p-1 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity bg-white/90 hover:bg-blue-50 rounded shadow-sm border border-slate-200/60"
        title={hasOrig ? "Edit value" : "Add target"}
        data-testid="cell-edit-trigger"
      >
        <Pencil className="h-3 w-3 text-blue-500" />
      </button>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────
export default function TargetManager({ token, targetType = "sales" }) {
  const [targets, setTargets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const isSales = targetType === "sales";
  const title = isSales ? "Sales Targets" : "Collection Targets";

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/targets/manage?target_type=${targetType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTargets(res.data.targets || []);
      setProjects(res.data.active_projects || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, targetType]);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  // Derive visible months — cap at current month (never show future)
  const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthsFromData = new Set();
  targets.forEach((t) => { if (t.period) monthsFromData.add(t.period.slice(0, 7)); });
  MONTHS.forEach((m) => monthsFromData.add(m.key));
  const visibleMonths = [...monthsFromData].filter((k) => k <= currentYm).sort().map((key) => {
    const [y, mo] = key.split("-").map(Number);
    return { value: `${key}-01`, key, label: new Date(y, mo - 1, 1).toLocaleString("en-IN", { month: "short" }), yearLabel: String(y).slice(2) };
  });

  // Build lookup
  const lookup = {};
  targets.forEach((t) => {
    lookup[`${t.project_name}::${(t.period || "").slice(0, 7)}`] = { value: t.target_value, count: t.target_count, period: t.period };
  });

  const projectsWithTargets = new Set(targets.map((t) => t.project_name));
  const displayProjects = projects.filter((p) => projectsWithTargets.has(p)).sort();
  targets.forEach((t) => { if (t.project_name && !displayProjects.includes(t.project_name)) displayProjects.push(t.project_name); });

  // Save handler — called by each cell
  const handleCellSave = async (projectName, period, amount, count) => {
    const headers = { Authorization: `Bearer ${token}` };
    if (amount === null) {
      await axios.delete(`${API}/targets/manage`, { data: { target_type: targetType, project_name: projectName, period }, headers });
      setTargets((prev) => prev.filter((t) => !(t.project_name === projectName && t.period === period)));
    } else {
      const body = { project_name: projectName, period, target_value: amount };
      if (isSales && count != null) body.target_count = count;
      await axios.post(`${API}/targets/manage?target_type=${targetType}`, body, { headers });
      setTargets((prev) => {
        const idx = prev.findIndex((t) => t.project_name === projectName && t.period === period);
        const entry = { project_name: projectName, period, target_value: amount, target_count: count };
        if (idx >= 0) { const next = [...prev]; next[idx] = entry; return next; }
        return [...prev, entry];
      });
    }
  };

  const showToast = (msg, type) => setToast({ message: msg, type });

  // Totals
  const colTotals = {};
  visibleMonths.forEach((m) => { colTotals[m.key] = 0; });
  const rowTotals = {};
  displayProjects.forEach((p) => {
    rowTotals[p] = 0;
    visibleMonths.forEach((m) => {
      const v = lookup[`${p}::${m.key}`]?.value || 0;
      rowTotals[p] += v;
      colTotals[m.key] += v;
    });
  });
  const grandTotal = Object.values(rowTotals).reduce((s, v) => s + v, 0);
  const fmtShort = (n) => (!n || n === 0) ? "–" : (n % 1 === 0 ? String(n) : n.toFixed(2));

  return (
    <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid={`target-manager-${targetType}`}>
      <CardHeader className="pb-3 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-600" />
            <CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: "Manrope" }}>
              {title} — Monthly Matrix
            </CardTitle>
          </div>
          <span className="text-[10px] text-slate-400">Hover a cell, click the pencil to edit</span>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-0">
        {loading && <div className="text-center py-8"><div className="inline-block h-5 w-5 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" /></div>}
        {!loading && displayProjects.length === 0 && <p className="text-xs text-slate-400 text-center py-8">No targets set yet.</p>}
        {!loading && displayProjects.length > 0 && (
          <div className="overflow-x-auto" data-testid="target-matrix-scroll">
            <table className="w-full text-xs border-collapse" style={{ tableLayout: "fixed" }} data-testid="target-matrix-table">
              <colgroup>
                <col style={{ width: "170px", minWidth: "170px" }} />
                {visibleMonths.map((m) => <col key={m.key} style={{ width: "82px", minWidth: "82px" }} />)}
                <col style={{ width: "80px", minWidth: "80px" }} />
              </colgroup>
              <thead>
                <tr className="bg-slate-50/80 sticky top-0 z-10">
                  <th className="sticky left-0 z-20 bg-slate-50 text-left py-2.5 px-3 font-semibold text-slate-600 border-b border-r border-slate-200 text-[11px]">Project</th>
                  {visibleMonths.map((m) => (
                    <th key={m.key} className="text-right py-2.5 pr-2 pl-1 font-semibold text-slate-500 border-b border-slate-200">
                      <div className="leading-tight"><span className="text-[11px] block">{m.label}</span><span className="text-[9px] text-slate-400 font-normal">{m.yearLabel}</span></div>
                    </th>
                  ))}
                  <th className="text-right py-2.5 pr-3 font-bold text-slate-700 border-b border-l border-slate-200 bg-slate-100/70 text-[11px]">Total</th>
                </tr>
              </thead>
              <tbody>
                {displayProjects.map((project, pi) => {
                  const bg = pi % 2 === 0 ? "#fff" : "#f8fafc";
                  return (
                    <tr key={project} className="hover:bg-blue-50/20 transition-colors" data-testid={`matrix-row-${project}`}>
                      <td className="sticky left-0 z-10 py-1 px-3 font-medium text-slate-800 border-r border-slate-100 whitespace-nowrap text-[11px] truncate" style={{ background: bg }} title={project}>{project}</td>
                      {visibleMonths.map((m) => {
                        const cell = lookup[`${project}::${m.key}`];
                        return (
                          <td key={m.key} className="py-0 px-0 border-l border-slate-100/60">
                            <MatrixCell
                              origValue={cell?.value}
                              origCount={cell?.count}
                              isSales={isSales}
                              projectName={project}
                              period={cell?.period || m.value}
                              onSaved={handleCellSave}
                              onToast={showToast}
                            />
                          </td>
                        );
                      })}
                      <td className="py-1 pr-3 text-right font-bold text-slate-700 border-l border-slate-200 bg-slate-100/40 tabular-nums text-[11px]">{fmtShort(rowTotals[project])}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[#0F1D32] text-white font-bold">
                  <td className="sticky left-0 z-10 py-2.5 px-3 bg-[#0F1D32] text-[11px] border-r border-white/10">Total</td>
                  {visibleMonths.map((m) => <td key={m.key} className="py-2.5 pr-2 text-right text-emerald-300 tabular-nums text-[11px] border-l border-white/5">{fmtShort(colTotals[m.key])}</td>)}
                  <td className="py-2.5 pr-3 text-right text-emerald-300 tabular-nums text-[11px] border-l border-white/10">{fmtShort(grandTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
    </Card>
  );
}
